import { Button, H4, Intent, Tag } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Banner } from "components/common/Banner";
import { FieldLabel } from "components/common/FieldLabel";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { segmentTrackClickDocumentation } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useSubscriptionForCluster } from "in_queue/contexts/ProjectDataContext";
import {
    clusterStatusFromSubscription,
    CLUSTER_STATUS_LABELS,
    isOpenClusterStatus
} from "in_queue/utils/clusterStatus";
import { getRelevantModelSet } from "in_queue/utils/customUpgradeValidation";
import { DateTime } from "luxon";
import { useLocation } from "react-router-dom";
import {
    getRedirectUrl,
    IN_QUEUE_COMMON_DOCS_SPACE,
    IN_QUEUE_REGION_DOCS_LOCATION
} from "types/gitBook";
import css from "./ClusterStatusBanner.module.scss";

export const ClusterStatusBanner: React.FC = () => {
    const cluster = useCluster();
    const loadingSubscription = useSubscriptionForCluster();
    const subscription =
        loadingSubscription === "loading" ? undefined : loadingSubscription;

    const status = clusterStatusFromSubscription(cluster, subscription);
    const modelSet = getRelevantModelSet(cluster, subscription);

    const user = useUserDataContext();
    const { pathname } = useLocation();
    const docsLink = getRedirectUrl(
        IN_QUEUE_COMMON_DOCS_SPACE,
        IN_QUEUE_REGION_DOCS_LOCATION[cluster.region] ?? null,
        user
    );

    return (
        <Banner
            className={css["ClusterStatusBanner"]}
            actionPlacement="top"
            action={
                <Button
                    variant="minimal"
                    intent={Intent.PRIMARY}
                    icon={IconNames.MANUAL}
                    text="Documentation"
                    disabled={docsLink === undefined}
                    onClick={() => {
                        if (docsLink !== undefined) {
                            segmentTrackClickDocumentation({
                                pagePath: pathname,
                                source: "cluster_banner"
                            });
                            window.open(docsLink, "_blank");
                        }
                    }}
                />
            }
            title={
                <Tag
                    minimal
                    round
                    intent={
                        isOpenClusterStatus(status)
                            ? Intent.PRIMARY
                            : Intent.NONE
                    }
                    className={css["ClusterStatusBanner-status"]}
                >
                    {CLUSTER_STATUS_LABELS[status]}
                </Tag>
            }
            description={
                <div className={css["ClusterStatusBanner-metrics"]}>
                    <div className={css["ClusterStatusBanner-metric"]}>
                        <FieldLabel>Latest Models</FieldLabel>
                        <H4 className={css["ClusterStatusBanner-value"]}>
                            {modelSet
                                ? DateTime.fromJSDate(
                                      modelSet.createdAt
                                  ).toLocaleString(DateTime.DATE_FULL)
                                : "-"}
                        </H4>
                    </div>
                    <div className={css["ClusterStatusBanner-metric"]}>
                        <FieldLabel>Model Set</FieldLabel>
                        <H4 className={css["ClusterStatusBanner-value"]}>
                            {modelSet?.name ?? "-"}
                        </H4>
                    </div>
                </div>
            }
        />
    );
};
